import { pantryColors } from '@pantry/ui'
import React from 'react'
import type { CampaignDisplayStatus } from '../../../../types/loyaltyMarketing'
import { STATUS_PILL_LABELS } from '../campaignDisplay'
import { EMERALD, EMERALD_BG } from './CampaignTypeIcon'

// Label map lives in campaignDisplay (pure, unit-tested) — re-exported here
// for the tab/drawer imports.
export { STATUS_PILL_LABELS }

const p = pantryColors

// Reference `.status-badge.{draft,scheduled,active,paused,completed}` tints —
// one shared pill for both the Campaigns and Flows tables so the two surfaces
// speak the same status language: Draft (gray) / Scheduled (accent blue) /
// Active (emerald, pulsing dot) / Paused (amber) / Completed (gray) / Failed (red).
export const STATUS_PILL_TINTS: Record<
  CampaignDisplayStatus,
  { fg: string; bg: string }
> = {
  draft: { fg: p.gray[500], bg: p.gray[100] },
  scheduled: { fg: p.brand.primary, bg: p.brand.light },
  preparing: { fg: p.brand.primary, bg: p.brand.light },
  running: { fg: EMERALD, bg: EMERALD_BG },
  paused: { fg: p.amber[700], bg: p.amber[100] },
  completed: { fg: p.gray[600], bg: p.gray[100] },
  failed: { fg: p.red[700], bg: p.red[50] },
}

// 2s opacity pulse for "live" dots (Active status, always-running automations).
export const PULSE_ANIMATION = 'mkt-pulse 2s ease-in-out infinite'
// Injects the keyframes into <head> once per page (module-level guard) so
// every pill/dot can use PULSE_ANIMATION without its own <style> tag.
let pulseInjected = false
const ensurePulseStyle = () => {
  if (pulseInjected || typeof document === 'undefined') return
  pulseInjected = true
  const el = document.createElement('style')
  el.setAttribute('data-mkt-pulse', '')
  el.textContent = '@keyframes mkt-pulse{0%,100%{opacity:1}50%{opacity:.35}}'
  document.head.appendChild(el)
}
export const PulseStyle: React.FC = () => {
  ensurePulseStyle()
  return null
}

export const StatusPill: React.FC<{ status: CampaignDisplayStatus }> = ({
  status,
}) => {
  const tint = STATUS_PILL_TINTS[status] ?? STATUS_PILL_TINTS.draft
  // Reference `.status-badge`: 11px/700 uppercase with .05em tracking and a
  // 6px dot before the label.
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 20,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        background: tint.bg,
        color: tint.fg,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: tint.fg,
          flexShrink: 0,
          animation: status === 'running' ? PULSE_ANIMATION : undefined,
        }}
      />
      {status === 'running' && <PulseStyle />}
      {STATUS_PILL_LABELS[status] ?? status}
    </span>
  )
}
